import { useQuery } from '@tanstack/react-query'
import { v2GetOutreachPricingOptions } from '@workspace/api-backend/react-query'
import { cn } from '@workspace/ui/lib/utils'
import { CalendarCheck, Check, Eye, Link2, RotateCcw, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useStartupMatchesQuery } from '../../../hooks/api'
import { renderTemplatePreview } from '../utils/render-template-preview'
import { WarmupSteps } from './outreach-warmup-steps'

/** Token values for the inline preview (first name, fund, link display). */
type PreviewResolve = { firstName?: string; fundName?: string; fundFocus?: string; link?: string }

interface PreviewContact {
  name: string
  title: string | null
  fund: string
}

// Shown only when the matcher hasn't produced contacts yet — the review screen
// normally renders well after matching, so this is a rare fallback.
const SAMPLE_CONTACT: PreviewContact = {
  name: 'Alex Rivera',
  title: 'Partner',
  fund: 'Northbound Capital',
}

interface Messages {
  msg1: string
  msg2: string
  msg3: string
}

type Step =
  | { kind: 'message'; label: string; text: string; withLink?: boolean }
  | { kind: 'reply'; from: string; text: string }
  | { kind: 'accept'; text: string }
  | { kind: 'charge'; icon: ReactNode; event: string; amount: string; emphasis?: boolean }
  | { kind: 'stop'; text: string }

interface Scenario {
  key: string
  tab: string
  contact: PreviewContact
  /** Total charged across the scenario, pre-formatted. */
  total: string
  steps: Step[]
}

export function OutreachConversationPreview({
  startupId,
  messages,
}: {
  startupId: string
  messages: Messages
}) {
  const matchesQuery = useStartupMatchesQuery(startupId, 10)
  const pricingQuery = useQuery(v2GetOutreachPricingOptions())
  const [selected, setSelected] = useState(0)

  const matched = extractContacts(matchesQuery.data?.data?.matches ?? [])
  const contacts = matched.length > 0 ? matched : [SAMPLE_CONTACT]
  const isSample = matched.length === 0

  const pricing = pricingQuery.data?.status === 'ok' ? pricingQuery.data.data : null
  const viewCents = pricing?.viewPriceCents ?? 200
  const returnCents = pricing?.returnVisitPriceCents ?? 500
  const callCents = pricing?.callBookedPriceCents ?? 0

  const scenarios = buildScenarios(messages, contacts, { viewCents, returnCents, callCents })
  const scenario = scenarios[Math.min(selected, scenarios.length - 1)]
  if (!scenario) return null
  const contact = scenario.contact

  return (
    <section className="mb-10">
      <div className="mb-3">
        <h2 className="text-[15px] font-semibold text-foreground">How investors see this</h2>
        <p className="mt-0.5 text-[12.5px] text-muted-foreground">
          Your messages in a real matched conversation, and exactly when your wallet is charged.
          Switch scenarios to see each outcome.
        </p>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-1.5 rounded-xl border border-border/60 bg-card/30 p-1.5">
        {scenarios.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSelected(i)}
            className={cn(
              'flex flex-col items-center gap-0.5 rounded-lg px-3 py-2 text-center transition-colors',
              i === selected
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:bg-card/60'
            )}
          >
            <span className="text-[12.5px] font-semibold">{s.tab}</span>
            <span
              className={cn(
                'text-[11px] tabular-nums',
                i === selected ? 'text-primary/80' : 'text-muted-foreground/70'
              )}
            >
              {s.total} charged
            </span>
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card/30">
        <header className="flex items-center gap-3 border-b border-border/40 bg-card/40 px-5 py-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border/40 bg-background/60 text-[12px] font-bold text-foreground/80">
            {initials(contact.name)}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[13.5px] font-semibold text-foreground">
              {contact.name}
            </div>
            <div className="truncate text-[11.5px] text-muted-foreground">
              {[contact.title, contact.fund].filter(Boolean).join(' · ')}
            </div>
          </div>
          {isSample && (
            <span className="ml-auto rounded-full border border-border/60 bg-card px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Example
            </span>
          )}
        </header>

        <div className="space-y-3 px-5 py-5">
          {scenario.steps.map((step, i) => (
            <StepRow key={`${scenario.key}-${i}`} step={step} />
          ))}
        </div>

        <footer className="border-t border-border/40 bg-card/40 px-5 py-2.5 text-center text-[11.5px] text-muted-foreground">
          You're only charged when an investor engages, never when we send a message.
        </footer>
      </div>
    </section>
  )
}

function StepRow({ step }: { step: Step }) {
  if (step.kind === 'message') {
    return <OutreachMessageBubble label={step.label} text={step.text} withLink={step.withLink} />
  }
  if (step.kind === 'reply') {
    return <OutreachMessageBubble incoming label={`${step.from} replies`} text={step.text} />
  }
  if (step.kind === 'accept') {
    return (
      <Divider>
        <Check className="size-3 text-emerald-400" strokeWidth={3} />
        {step.text}
      </Divider>
    )
  }
  if (step.kind === 'stop') {
    return (
      <Divider>
        <X className="size-3 text-muted-foreground/70" strokeWidth={3} />
        {step.text}
        <span className="rounded-full border border-border/60 bg-card px-2 py-0.5 font-semibold tabular-nums text-muted-foreground">
          $0
        </span>
      </Divider>
    )
  }
  return (
    <div className="flex items-center justify-center gap-2 text-[11.5px] text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-muted-foreground/70">{step.icon}</span>
        {step.event}
      </span>
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums',
          step.emphasis
            ? 'bg-primary/15 text-primary'
            : 'border border-border/60 bg-card text-foreground/80'
        )}
      >
        {step.amount} charged
      </span>
    </div>
  )
}

export function OutreachMessageBubble({
  label,
  text,
  withLink,
  incoming,
  resolve,
}: {
  label: string
  text: string
  withLink?: boolean
  incoming?: boolean
  /**
   * When provided, the text carries inline tokens ([FirstName]/[Link]/…) and is
   * rendered via the shared preview (the [Link] token stays highlighted inline,
   * matching the review-messages dialog) — the separate link chip is suppressed.
   * When omitted, the text is pre-substituted plain and the chip shows the link.
   */
  resolve?: PreviewResolve
}) {
  return (
    <div className={cn('flex flex-col', incoming ? 'items-start' : 'items-end')}>
      <span className="mb-1 text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground/70">
        {label}
      </span>
      <div
        className={cn(
          'max-w-[85%] px-4 py-2.5',
          incoming
            ? 'rounded-2xl rounded-tl-sm border border-border/60 bg-card'
            : 'rounded-2xl rounded-tr-sm border border-primary/20 bg-primary/10'
        )}
      >
        {text.trim() ? (
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/90">
            {resolve ? renderTemplatePreview(text, resolve) : text}
          </p>
        ) : (
          <p className="text-[13px] italic text-muted-foreground/70">No message yet</p>
        )}
        {withLink && !resolve && (
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border/50 bg-background/60 px-2 py-1 text-[11px] text-muted-foreground">
            <Link2 className="size-3 text-primary" />
            your-seedforge-profile.link
          </span>
        )}
      </div>
    </div>
  )
}

function Divider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-0.5 text-[11.5px] text-muted-foreground">
      {children}
    </div>
  )
}

// The read-only conversation preview shared between the campaign-roster contact
// dialog and the message editor: automated warm-up, then the three-message
// sequence with the accept / no-reply dividers between them. The texts arrive
// pre-built (greeting + fund substitution already applied) so this stays a pure
// presentational component.
export function OutreachConversationSequence({
  firstName,
  connectionText,
  followUpText,
  secondFollowUpText,
  resolve,
}: {
  firstName: string
  connectionText: string
  followUpText: string
  secondFollowUpText: string
  /**
   * When provided, the texts carry inline tokens and render via the shared
   * preview (inline-highlighted [Link], no chip) — matching the review dialog.
   */
  resolve?: PreviewResolve
}) {
  return (
    <div className="space-y-2.5">
      <WarmupSteps labelAlign="right" />
      <OutreachMessageBubble
        label="Connection request"
        text={connectionText}
        withLink
        resolve={resolve}
      />
      <Divider>
        <Check className="size-3 text-emerald-400" strokeWidth={3} />
        {firstName} accepts your request
      </Divider>
      <OutreachMessageBubble
        label="Follow-up · after they accept"
        text={followUpText}
        withLink
        resolve={resolve}
      />
      <Divider>If no reply after a few days</Divider>
      <OutreachMessageBubble label="Second follow-up" text={secondFollowUpText} resolve={resolve} />
    </div>
  )
}

function buildScenarios(
  messages: Messages,
  contacts: PreviewContact[],
  prices: { viewCents: number; returnCents: number; callCents: number }
): Scenario[] {
  const contactFor = (i: number) => contacts[i] ?? contacts[0] ?? SAMPLE_CONTACT
  const view = formatUsd(prices.viewCents)
  const ret = formatUsd(prices.returnCents)
  const call = formatUsd(prices.callCents)

  const connectionRequest = (contact: PreviewContact): Step => ({
    kind: 'message',
    label: 'Connection request',
    text: `Hey ${first(contact)}, ${messages.msg1}`,
    withLink: true,
  })
  // The follow-up template carries its own greeting, link frame, and closing
  // CTA inline; shown as-is.
  const followUpStep = (contact: PreviewContact): Step => ({
    kind: 'message',
    label: 'Follow-up',
    text: `Hey ${first(contact)}, ${messages.msg2}`,
    withLink: true,
  })
  const secondFollowUpStep = (contact: PreviewContact): Step => ({
    kind: 'message',
    label: 'Second follow-up',
    text: `Hey ${first(contact)}, ${messages.msg3}`,
    withLink: false,
  })

  const happy = contactFor(0)
  const reads = contactFor(1)
  const none = contactFor(2)

  return [
    {
      key: 'call',
      tab: 'Books a call',
      contact: happy,
      total: formatUsd(prices.viewCents + prices.returnCents + prices.callCents),
      steps: [
        connectionRequest(happy),
        { kind: 'accept', text: `${first(happy)} accepts your request` },
        followUpStep(happy),
        {
          kind: 'charge',
          icon: <Eye className="size-3.5" />,
          event: 'opens your SeedForge Profile (60s+)',
          amount: view,
        },
        secondFollowUpStep(happy),
        {
          kind: 'charge',
          icon: <RotateCcw className="size-3.5" />,
          event: 'comes back to read more',
          amount: ret,
        },
        {
          kind: 'reply',
          from: first(happy),
          text: "Really impressed by the traction. This fits what we're looking for. Could we grab 30 minutes next week? Happy to dig into the numbers.",
        },
        {
          kind: 'charge',
          icon: <CalendarCheck className="size-3.5" />,
          event: 'books a call with you',
          amount: call,
          emphasis: true,
        },
      ],
    },
    {
      key: 'reads',
      tab: 'Opens, then passes',
      contact: reads,
      total: view,
      steps: [
        connectionRequest(reads),
        { kind: 'accept', text: `${first(reads)} accepts your request` },
        followUpStep(reads),
        {
          kind: 'charge',
          icon: <Eye className="size-3.5" />,
          event: 'opens your SeedForge Profile (60s+)',
          amount: view,
        },
        {
          kind: 'reply',
          from: first(reads),
          text: 'Thanks for sharing, took a look through your profile. Not a fit for our thesis right now, but keep me posted on the next round.',
        },
      ],
    },
    {
      key: 'none',
      tab: 'No engagement',
      contact: none,
      total: formatUsd(0),
      steps: [
        connectionRequest(none),
        { kind: 'accept', text: `${first(none)} accepts your request` },
        followUpStep(none),
        secondFollowUpStep(none),
        { kind: 'stop', text: `${first(none)} never opens your SeedForge Profile` },
      ],
    },
  ]
}

function extractContacts(
  matches: { investorName: string; investorContacts: { name: string; title: string | null }[] }[]
): PreviewContact[] {
  const out: PreviewContact[] = []
  for (const match of matches) {
    for (const contact of match.investorContacts ?? []) {
      out.push({ name: contact.name, title: contact.title, fund: match.investorName })
      if (out.length >= 3) return out
    }
  }
  return out
}

function first(contact: PreviewContact): string {
  return contact.name.split(' ')[0] ?? contact.name
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  )
}

function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}
